/**
 * Translation context adapter on top of react-i18next.
 *
 * Keeps the legacy API (`useTranslation()` returning { t, currentLanguage,
 * setLanguage, availableLanguages }) so existing callers across the codebase
 * keep working unchanged. Under the hood it delegates to i18next, which gives
 * us proper namespacing, ICU, plural support, and missing-key warnings.
 *
 * Key behaviour preserved from the original implementation:
 *  - localStorage key 'app_language' drives persistence.
 *  - Switching the language also resets the saved currency and reloads the
 *    page so currency-aware contexts re-initialise (see CurrencyProvider).
 *  - Legacy `{paramName}` placeholders are still supported in addition to
 *    i18next's native `{{paramName}}` interpolation.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';

import i18n, {
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from '../i18n';

type Language = SupportedLanguage;

export interface TranslationContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  availableLanguages: Language[];
}

export const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

interface TranslationProviderProps {
  children: ReactNode;
}

const getDefaultCurrencyForLanguage = (language: Language): string => {
  switch (language) {
    case 'tr':
      return 'TRY';
    case 'de':
      return 'EUR';
    case 'en':
    default:
      return 'USD';
  }
};

export const normaliseLegacyPlaceholders = (
  value: string,
  params?: Record<string, string | number>,
): string => {
  if (!params) return value;
  let out = value;
  Object.entries(params).forEach(([k, v]) => {
    out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  });
  return out;
};

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ children }) => {
  const { t: i18nT, i18n: i18nInstance } = useI18nextTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    (i18nInstance.resolvedLanguage as Language) ||
      (i18nInstance.language as Language) ||
      'en',
  );

  useEffect(() => {
    const handler = (lng: string) => {
      if ((SUPPORTED_LANGUAGES as readonly string[]).includes(lng)) {
        setCurrentLanguage(lng as Language);
      }
    };
    i18nInstance.on('languageChanged', handler);
    return () => {
      i18nInstance.off('languageChanged', handler);
    };
  }, [i18nInstance]);

  // Sayfanın dili seçili dil: tarayıcı Türkçe sayfayı "İngilizce" sanıp
  // çevirmeye kalkmasın (index.html'deki translate="no" ile birlikte).
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    localStorage.setItem('selectedCurrency', getDefaultCurrencyForLanguage(lang));
    i18n.changeLanguage(lang).finally(() => {
      // Reload to refresh currency-aware contexts that don't observe storage events.
      window.location.reload();
    });
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const result = i18nT(key, params as Record<string, unknown>);
      // i18next returns the key when missing; preserve that signal but also
      // accept legacy `{name}` placeholders coming from the old JSON format.
      return normaliseLegacyPlaceholders(result as string, params);
    },
    [i18nT],
  );

  (t as any).i18n = i18nInstance;

  const value: TranslationContextType = {
    t,
    currentLanguage,
    setLanguage,
    availableLanguages: SUPPORTED_LANGUAGES as unknown as Language[],
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
